import { useState } from "react"
import { Paper, Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core"
import WarningIcon from "@material-ui/icons/Warning"
import ExpandMoreIcon from "@material-ui/icons/ExpandMore"
import ExpandLessIcon from "@material-ui/icons/ExpandLess"
import { EditContractType } from "../../../types/contractTypes"
import s from "./AmountMismatchWarning.module.scss"

type AmountMismatchWarningPropsType = {
    contractData: EditContractType
}

type MismatchRow = {
    contractAmount: number | null
    contractDate: string | null
    checkValue: number | null
    checkDate: string | null
}

export default function AmountMismatchWarning({ contractData }: AmountMismatchWarningPropsType) {
    const [isExpanded, setIsExpanded] = useState<boolean>(false)

    // Build amounts list — mirrors backend CheckAmountsValidityAsync logic
    const amounts: Array<{ amount: number | null; date: string | null }> = []

    const parentSource = contractData.amountApproved ?? contractData.amountRequested
    amounts.push({
        amount: parentSource?.amount ?? null,
        date: parentSource?.date ?? null,
    })

    const sortedChildren = [...(contractData.childContracts ?? [])].sort((a, b) => {
        const dateA = a.amountRequested?.date ? new Date(a.amountRequested.date).getTime() : 0
        const dateB = b.amountRequested?.date ? new Date(b.amountRequested.date).getTime() : 0
        return dateA - dateB
    })

    sortedChildren.forEach(child => {
        amounts.push({
            amount: child.amountRequested?.amount ?? null,
            date: child.amountRequested?.date ?? null,
        })
    })

    // Build checks list — CheckSent status records sorted by dateChange then id
    const checks = [...(contractData.contractStatus ?? [])]
        .filter(cs => cs.contractsStatus === "CheckSent")
        .sort((a, b) => {
            const dateDiff = new Date(a.dateChange).getTime() - new Date(b.dateChange).getTime()
            if (dateDiff !== 0) return dateDiff
            return (a.id ?? 0) - (b.id ?? 0)
        })
        .map(cs => ({ value: cs.statusValue, date: cs.dateChange }))

    // Zip both lists into table rows, padding the shorter side with nulls
    const rowCount = Math.max(amounts.length, checks.length)
    const rows: MismatchRow[] = Array.from({ length: rowCount }, (_, i) => ({
        contractAmount: amounts[i]?.amount ?? null,
        contractDate: amounts[i]?.date ?? null,
        checkValue: checks[i]?.value ?? null,
        checkDate: checks[i]?.date ?? null,
    }))

    const formatDate = (dateStr: string | null): string => {
        if (!dateStr) return "—"
        return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" })
    }

    const formatAmount = (amount: number | null): string => {
        if (amount === null || amount === undefined) return "—"
        return `$${amount.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
    }

    const isRowMismatch = (row: MismatchRow): boolean =>
        row.contractAmount !== row.checkValue || row.contractAmount === null || row.checkValue === null

    return (
        <section className={s["AmountMismatchWarning"]}>
            <div className={s["AmountMismatchWarning-Header"]} onClick={() => setIsExpanded(!isExpanded)}>
                <div className={s["AmountMismatchWarning-HeaderLeft"]}>
                    <WarningIcon className={s["AmountMismatchWarning-Icon"]} />
                    <span>Amount / Check Mismatch Detected</span>
                </div>
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </div>
            {isExpanded && (
                <Paper className={s["AmountMismatchWarning-Body"]} elevation={0} square>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={s["AmountMismatchWarning-HeaderCell"]}>Contract Amount</TableCell>
                                <TableCell className={s["AmountMismatchWarning-HeaderCell"]}>Contract Date</TableCell>
                                <TableCell className={s["AmountMismatchWarning-HeaderCell"]}>Check Value</TableCell>
                                <TableCell className={s["AmountMismatchWarning-HeaderCell"]}>Check Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, index) => (
                                <TableRow
                                    key={index}
                                    className={isRowMismatch(row) ? s["AmountMismatchWarning-Row--Mismatch"] : ""}
                                >
                                    <TableCell>{formatAmount(row.contractAmount)}</TableCell>
                                    <TableCell>{formatDate(row.contractDate)}</TableCell>
                                    <TableCell>{formatAmount(row.checkValue)}</TableCell>
                                    <TableCell>{formatDate(row.checkDate)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </section>
    )
}
